import React, { useState, useEffect } from "react";
import {
  Modal,
  Spin,
  Table,
  Tag,
  Space,
  Button,
  message,
  Typography,
  Switch,
  Tooltip,
  Card,
} from "antd";
import { CopyOutlined } from "@ant-design/icons";
import moment from "moment";
import { ChatCompletionRequest } from "../../model_gen/chat";
import { RequestsService } from "../../services/RequestsService";
import RequestViewModal from "./RequestViewModal";
import { RequestViewButton } from "../shared/button/Buttons";

const { Text, Paragraph } = Typography;

interface RequestRepresentationModalProps {
  requestId: string;
  isVisible: boolean;
  onClose: () => void;
  apiKey: string;
}

const RequestRepresentationModal: React.FC<RequestRepresentationModalProps> = ({
  requestId,
  isVisible,
  onClose,
  apiKey,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [representationData, setRepresentationData] = useState<any>(null);
  const [selectedSimilarRequests, setSelectedSimilarRequests] = useState<
    ChatCompletionRequest[]
  >([]);
  const [showOnlySelected, setShowOnlySelected] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  // Modal state for request view
  const [isRequestViewModalVisible, setIsRequestViewModalVisible] =
    useState(false);
  const [selectedRequest, setSelectedRequest] =
    useState<ChatCompletionRequest | null>(null);

  useEffect(() => {
    if (isVisible && requestId) {
      setIsLoading(true);
      RequestsService.getRequestRepresentation(
        requestId,
        setIsLoading,
        apiKey
      ).subscribe(
        (data: any) => {
          setRepresentationData(data);
        },
        (error: any) => {
          console.error(error);
          message.error("Failed to fetch request representation");
          setIsLoading(false);
        }
      );
    }
  }, [isVisible, requestId, apiKey]);

  const handleClose = () => {
    setRepresentationData(null);
    setSelectedSimilarRequests([]);
    setShowOnlySelected(false);
    setCurrentPage(1);
    onClose();
  };

  const handleViewRequest = (request: ChatCompletionRequest) => {
    setSelectedRequest(request);
    setIsRequestViewModalVisible(true);
  };

  const handleCloseRequestViewModal = () => {
    setIsRequestViewModalVisible(false);
    setSelectedRequest(null);
  };

  const similarRequestsColumns = [
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "20%",
      render: (text: number) => moment(text).format("YYYY-MM-DD hh:mm:ss A"),
    },
    {
      title: (
        <div style={{ display: "flex", alignItems: "start" }}>
          Tags
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            style={{ marginLeft: "4px", color: "#8c8c8c" }}>
            <path
              fill="currentColor"
              d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
            />
          </svg>
        </div>
      ),
      key: "tags",
      width: "40%",
      render: (_: any, record: ChatCompletionRequest) => {
        try {
          if (record.metadata?._maitai_request_meta) {
            const metaData = JSON.parse(
              record.metadata._maitai_request_meta as string
            );
            const tags = metaData.tags || [];
            return (
              <Space wrap>
                {tags.map((tag: string) => (
                  <Tag key={tag} bordered={true}>
                    {tag}
                  </Tag>
                ))}
                {tags.length === 0 && (
                  <span style={{ color: "#999" }}>No tags</span>
                )}
              </Space>
            );
          }
          return <span style={{ color: "#999" }}>No tags</span>;
        } catch (e) {
          console.error("Error parsing metadata:", e);
          return <span style={{ color: "#999" }}>No tags</span>;
        }
      },
    },
    {
      title: "Request ID",
      key: "request_id",
      width: "25%",
      render: (_: any, record: ChatCompletionRequest) => {
        const requestId = record.request_id || "";
        const truncatedId = `...${requestId.slice(-8)}`;
        return (
          <Space style={{ width: "100%" }}>
            {truncatedId}
            <Button
              icon={<CopyOutlined />}
              size="small"
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(requestId);
                message.success("Request ID copied to clipboard");
              }}
            />
          </Space>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: "15%",
      render: (_: any, record: ChatCompletionRequest) => (
        <Space>
          <RequestViewButton
            apiKey={apiKey}
            request={record}
            isEditable={false}
            onClick={() => handleViewRequest(record)}
            onUpdated={() => {}}>
            View
          </RequestViewButton>
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title={
        <Typography.Title level={3} style={{ margin: 0 }}>
          Request Representation
        </Typography.Title>
      }
      open={isVisible}
      onCancel={handleClose}
      footer={null}
      width={1200}>
      {isLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "24px",
          }}>
          <Spin />
        </div>
      ) : representationData ? (
        <div>
          {/* Full Summary Section */}
          <div style={{ marginBottom: "36px" }}>
            <Text
              strong
              style={{
                fontSize: "16px",
                marginBottom: "12px",
                display: "block",
              }}>
              Summary
            </Text>
            <Card
              style={{
                backgroundColor: "#f0f5ff",
                border: "1px solid #d6e4ff",
              }}
              bodyStyle={{
                padding: "16px",
                fontSize: "14px",
                color: "#1d39c4",
              }}>
              <Paragraph style={{ margin: 0 }}>
                {(() => {
                  try {
                    const metaData = JSON.parse(
                      representationData.target_request.metadata
                        ._maitai_request_meta
                    );
                    return metaData.full_summary || "No summary available";
                  } catch (e) {
                    console.error("Error parsing metadata:", e);
                    return "No summary available";
                  }
                })()}
              </Paragraph>
            </Card>
          </div>

          {/* Dataset Representation Section */}
          <div style={{ marginBottom: "36px" }}>
            <Text
              strong
              style={{
                fontSize: "16px",
                marginBottom: "12px",
                display: "block",
              }}>
              Dataset Coverage
            </Text>
            <Space wrap>
              {(() => {
                try {
                  const datasetsRepresentation = JSON.parse(
                    representationData.target_request.metadata
                      .datasets_representation
                  );
                  return Object.entries(datasetsRepresentation).map(
                    ([datasetId, value]: [string, any]) => (
                      <Tooltip
                        key={datasetId}
                        title={`Similar Requests: ${value.similar_requests_count}`}>
                        <Tag
                          color={
                            value.highest_score >= 80
                              ? "success"
                              : value.highest_score >= 60
                              ? "warning"
                              : "error"
                          }
                          style={{ padding: "4px 8px" }}>
                          {datasetId}: {Number(value.highest_score).toFixed(0)}%
                        </Tag>
                      </Tooltip>
                    )
                  );
                } catch (e) {
                  console.error("Error parsing datasets_representation:", e);
                  return <Tag>No dataset data available</Tag>;
                }
              })()}
            </Space>
          </div>

          <div>
            <Text
              strong
              style={{
                fontSize: "16px",
                marginBottom: "16px",
                display: "block",
              }}>
              Similar Requests
            </Text>
            <div
              style={{
                marginBottom: 16,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Switch
                  checked={showOnlySelected}
                  onChange={setShowOnlySelected}
                  style={{ marginRight: 8 }}
                />
                <span>Show Only Selected</span>
              </div>
            </div>
            {representationData.similar_requests &&
            representationData.similar_requests.length > 0 ? (
              <Table
                rowKey="request_id"
                columns={similarRequestsColumns}
                dataSource={
                  showOnlySelected
                    ? selectedSimilarRequests
                    : representationData.similar_requests
                }
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: showOnlySelected
                    ? selectedSimilarRequests.length
                    : representationData.similar_requests.length,
                  onChange: (page, size) => {
                    setCurrentPage(page);
                    if (size) setPageSize(size);
                  },
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                }}
                rowSelection={{
                  selectedRowKeys: selectedSimilarRequests.map(
                    (r) => r.request_id
                  ),
                  onChange: (_, selectedRows) => {
                    setSelectedSimilarRequests(selectedRows);
                  },
                }}
                size="small"
              />
            ) : (
              <Text>No similar requests found</Text>
            )}
          </div>
        </div>
      ) : (
        <Text>No representation data available</Text>
      )}

      {selectedRequest && (
        <RequestViewModal
          request={selectedRequest}
          isVisible={isRequestViewModalVisible}
          onClose={handleCloseRequestViewModal}
          apiKey={apiKey}
        />
      )}
    </Modal>
  );
};

export default RequestRepresentationModal;
